/* eslint-disable max-statements */
import abi from 'ethereumjs-abi';
import BN from 'bn.js';
import * as web3 from 'web3-utils';
import { blockchainNumberToSystemCurrency, CurrencyType, systemCurrencyToBlockchainNumber } from '@wolffun/system-currency';
import { THETA_MARKETPLACE_WALLET_ADDRESS } from 'src/constants';
import thetanCoinContract from '@wolffun/shared/src/contracts/thetaCoin';
import busdContract from '@wolffun/shared/src/contracts/busd';
import wbnbContract from '@wolffun/shared/src/contracts/wbnb';
import thetanGemContract from '@wolffun/shared/src/contracts/thetaGem';
import thetanSmartContract from '@wolffun/shared/src/contracts/thetanMarketSmartContract';
import nftSmartContract from '@wolffun/shared/src/contracts/nftSmartContract';
import rentalSmartContract from '@wolffun/shared/src/contracts/rentalSmartContract';
import stakingSmartContract from '@wolffun/shared/src/contracts/stakingSmartContract';
import thetanBoxSmartContract from '@wolffun/shared/src/contracts/thetanBoxSmartContract';
import privateSaleSmartContract from '@wolffun/shared/src/contracts/privateSaleSmartContract';
import mintHeroSmartContract from '@wolffun/shared/src/contracts/mintHeroSmartContract';
import mintCosmeticSmartContract from '@wolffun/shared/src/contracts/mintCosmeticSmartContract';
import fusionSmartContract from '@wolffun/shared/src/contracts/fusionSmartContract';
import { EipError } from 'src/redux/types';
import EventEmitter from './eventEmitter';
import { ensureProvider } from '@wolffun/shared/src/utils/ethereum';
import { getCurrentConnector } from '@wolffun/shared/src/utils/walletClient';
import { getGasPrice } from '@wolffun/shared/src/utils/ethereum.util';

const getContract = type => {
  switch (type) {
    case CurrencyType.BUSD:
      return busdContract;

    case CurrencyType.WBNB:
      return wbnbContract;

    case CurrencyType.THC:
      return thetanCoinContract;

    case CurrencyType.THG:
      return thetanGemContract;

    default:
      throw new Error('Currency contract is not supported!');
  }
};

export const getNativeBalance = async address => {
  const {
    error
  } = await ensureProvider();

  if (error !== EipError.NO_ERROR) {
    return {
      error
    };
  }

  try {
    const balance = await getCurrentConnector().fetchBalance(address);
    return {
      error: EipError.NO_ERROR,
      balance: blockchainNumberToSystemCurrency(CurrencyType.BNB, balance)
    };
  } catch (err) {
    return {
      error: err.code
    };
  }
};
export const addThetaCoinAsset = async () => {
  const {
    error
  } = await ensureProvider();

  if (error !== EipError.NO_ERROR) {
    return {
      error,
      wasAdded: false
    };
  }

  try {
    const wasAdded = await getCurrentConnector().watchAsset({
      address: thetanCoinContract.address,
      symbol: thetanCoinContract.symbol
    });
    return {
      error: EipError.NO_ERROR,
      wasAdded: !!wasAdded
    };
  } catch (err) {
    return {
      error: err.code,
      wasAdded: false
    };
  }
};
export { getBalance, getThetaBalance, personalSign, getAccounts, switchToBSC } from '@wolffun/shared/src/utils/ethereum';
export const removeAllowance = async ({
  contractAddress,
  fromAddress,
  token
}) => {
  const contract = await getContract(token).getContract();

  try {
    const gasPrice = await getGasPrice();
    const allowance = await contract.methods.allowance(fromAddress, contractAddress).call();
    const allowanceSC = blockchainNumberToSystemCurrency(CurrencyType.THC, new BN(allowance));
    const allowanceBN = new BN(allowanceSC.value);
    const amountBN = new BN('0');

    if (allowanceBN.gt(amountBN)) {
      await contract.methods.approve(contractAddress, systemCurrencyToBlockchainNumber({
        token: token,
        value: '0'
      })).send({
        from: fromAddress,
        gasPrice
      });
    }

    return {
      error: EipError.NO_ERROR
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNSUPPORTED_METHOD
      };
    }
  }
};
export const getAllowance = async ({
  contractAddress,
  fromAddress,
  token
}) => {
  const contract = await getContract(token).getContract();

  try {
    const allowance = await contract.methods.allowance(fromAddress, contractAddress).call();
    const allowanceSC = blockchainNumberToSystemCurrency(CurrencyType.THC, new BN(allowance));
    return {
      error: EipError.NO_ERROR,
      allowance: allowanceSC
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNSUPPORTED_METHOD
      };
    }
  }
};
export const approveContract = async (address, amount, toAddress) => {
  const contract = await getContract(amount.token).getContract();

  try {
    const gasPrice = await getGasPrice();
    await contract.methods.approve(toAddress || THETA_MARKETPLACE_WALLET_ADDRESS, systemCurrencyToBlockchainNumber({
      token: amount.token,
      value: ['1', ...new Array(30).fill('0')].join('')
    })).send({
      from: address,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNSUPPORTED_METHOD
      };
    }
  }
};
export const approveUseThetaBalance = async (address, amount, toAddress) => {
  const contract = await getContract(amount.token).getContract();

  try {
    const gasPrice = await getGasPrice();
    const allowance = await contract.methods.allowance(address, toAddress || THETA_MARKETPLACE_WALLET_ADDRESS).call();
    const allowanceSC = blockchainNumberToSystemCurrency(CurrencyType.THC, new BN(allowance));
    const allowanceBN = new BN(allowanceSC.value);
    const amountBN = new BN(amount.value);

    if (allowanceBN.lt(amountBN)) {
      await contract.methods.approve(toAddress || THETA_MARKETPLACE_WALLET_ADDRESS, systemCurrencyToBlockchainNumber({
        token: amount.token,
        value: ['1', ...new Array(30).fill('0')].join('')
      })).send({
        from: address,
        gasPrice
      });
    }

    return {
      error: EipError.NO_ERROR
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNSUPPORTED_METHOD
      };
    }
  }
};
export const checkoutItem = async ({
  senderAddress,
  price,
  blockchainId,
  paymentTokenAddress
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await thetanSmartContract.getContract()).methods.buy(blockchainId, systemCurrencyToBlockchainNumber(price), paymentTokenAddress).send({
      from: senderAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const rentItem = async ({
  senderAddress,
  tokenId,
  price
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await rentalSmartContract.getContract()).methods.renting(tokenId, systemCurrencyToBlockchainNumber(price)).send({
      from: senderAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const getOwnerOfItem = async tokenId => {
  try {
    const data = await (await nftSmartContract.getContract()).methods.ownerOf(tokenId).call();
    return {
      error: EipError.NO_ERROR,
      ownerAddress: String(data)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code,
        ownerAddress: ''
      };
    } else {
      return {
        error: EipError.UNNKOWN,
        ownerAddress: ''
      };
    }
  }
};
export const checkItemLocked = async tokenId => {
  try {
    const data = await (await nftSmartContract.getContract()).methods.isLocked(tokenId).call();
    return {
      error: EipError.NO_ERROR,
      isLocked: Boolean(data)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code,
        isLocked: false
      };
    } else {
      return {
        error: EipError.UNNKOWN,
        isLocked: false
      };
    }
  }
};
export const buyPrivateSale = async ({
  blockChainId,
  busdAmount,
  expiredAt,
  signature,
  thcAmount,
  buyerAddress
}) => {
  try {
    const gasPrice = await getGasPrice();
    const privateSale = (await privateSaleSmartContract.getContract()).methods.buyPrivateSale(blockChainId, busdAmount, thcAmount, expiredAt, signature);
    await privateSale.send({
      from: buyerAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const stopRentOutItem = async ({
  senderAddress,
  tokenId
}) => {
  try {
    const gasPrice = await getGasPrice();
    const ignoreSignature = (await rentalSmartContract.getContract()).methods.cancelLending(tokenId);
    await ignoreSignature.send({
      from: senderAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const confirmStake = async ({
  id,
  amount,
  lockedDuration,
  expiredAt,
  tokenIds,
  signature,
  accountAddress
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await stakingSmartContract.getContract()).methods.stake(id, amount, lockedDuration, expiredAt, tokenIds, signature).send({
      from: accountAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const buyThetanBox = async ({
  id,
  boxType,
  price,
  paymentErc20,
  expiredAt,
  signature,
  accountAddress
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await thetanBoxSmartContract.getContract()).methods.buyBoxWithSignature(id, boxType, price, paymentErc20, expiredAt, signature).send({
      from: accountAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const claimGiftBox = async ({
  id,
  boxType,
  price,
  paymentErc20,
  expiredAt,
  signature,
  accountAddress
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await thetanBoxSmartContract.getContract()).methods.buyBoxFreeWithSignature(id, boxType, price, paymentErc20, expiredAt, signature).send({
      from: accountAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const claimWheelTicket = async ({
  id,
  boxType,
  price,
  paymentErc20,
  expiredAt,
  signature,
  accountAddress
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await thetanBoxSmartContract.getContract()).methods.buyBoxFreeWithSignature(id, boxType, price, paymentErc20, expiredAt, signature).send({
      from: accountAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const unStaking = async ({
  signature,
  expiredAt,
  id,
  senderAddress
}) => {
  try {
    const gasPrice = await getGasPrice();
    const unStaking = (await stakingSmartContract.getContract()).methods.withdraw(web3.toBN(id), web3.toBN(expiredAt), signature);
    await unStaking.send({
      from: senderAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const confirmFuse = async ({
  id,
  amount,
  expiredAt,
  tokenIds,
  signature,
  accountAddress
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await fusionSmartContract.getContract()).methods.fuse(id, amount, expiredAt, tokenIds, signature).send({
      from: accountAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const lending = async props => {
  try {
    const gasPrice = await getGasPrice();
    const {
      senderAddress,
      systemPrice,
      signature,
      blockchainId,
      tokenId,
      expiredAt
    } = props;
    const data = await (await rentalSmartContract.getContract()).methods.lending(blockchainId, tokenId, systemCurrencyToBlockchainNumber(systemPrice), expiredAt, signature).send({
      from: senderAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: data
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const unlockHero = async ({
  senderAddress,
  tokenId,
  bid,
  expiredAt,
  signature,
  old
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await rentalSmartContract.getContract()).methods.unlockHero(bid, tokenId, old, expiredAt, signature).send({
      from: senderAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const mintHero = async ({
  senderAddress,
  tokenId,
  bid,
  expiredAt,
  signature
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await mintHeroSmartContract.getContract()).methods.mint(bid, tokenId, expiredAt, signature).send({
      from: senderAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const mintCosmetic = async ({
  senderAddress,
  tokenId,
  bid,
  expiredAt,
  signature
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await mintCosmeticSmartContract.getContract()).methods.mint(bid, tokenId, expiredAt, signature).send({
      from: senderAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const sellItem = async props => {
  try {
    const {
      senderAddress,
      systemPrice,
      signature,
      blockchainId,
      nftAddress,
      tokenId,
      expiredAt,
      paymentTokenAddress
    } = props;
    const gasPrice = await getGasPrice();
    const data = await (await thetanSmartContract.getContract()).methods.selling(blockchainId, nftAddress, paymentTokenAddress, tokenId, systemCurrencyToBlockchainNumber(systemPrice), expiredAt, signature).send({
      from: senderAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (err) {
    if (err.code) {
      return {
        error: err.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const cancelSellingItem = async ({
  blockchainId,
  senderAddress
}) => {
  try {
    const gasPrice = await getGasPrice();
    const data = await (await thetanSmartContract.getContract()).methods.cancelSelling(blockchainId).send({
      from: senderAddress,
      gasPrice
    });
    return {
      error: EipError.NO_ERROR,
      data: String(data === null || data === void 0 ? void 0 : data.transactionHash)
    };
  } catch (error) {
    if (error.code) {
      return {
        error: error.code
      };
    } else {
      return {
        error: EipError.UNNKOWN
      };
    }
  }
};
export const hashMessage = (args, data) => {
  return '0x' + abi.soliditySHA3(args, data).toString('hex');
};
export const addChainChangedListener = (() => {
  const emitter = new EventEmitter();
  getCurrentConnector().on('chainChanged', emitter.emit);
  return listener => emitter.add(listener);
})();
export const addAccountChangedListener = (() => {
  const emitter = new EventEmitter();
  getCurrentConnector().on('accountsChanged', emitter.emit);
  return listener => emitter.add(listener);
})();